// Import models
import type {
  AddNote200Response,
  AddNoteRequestContent,
  BadRequestErrorResponseContent,
  CreatePlayer200Response,
  CreatePlayerMobile200Response,
  CreatePlayerMobileRequestContent,
  CreatePlayerRequestContent,
  DecrementLoyaltyPointsRequestContent,
  DeleteNote200Response,
  GenericSuccessResponseContent,
  GetBilling200Response,
  GetBilling404Response,
  GetLoyaltyPoints200Response,
  GetNotes200Response,
  GetPasLastViewed200Response,
  GetPlayerActivityStatement200Response,
  GetPlayerDetails200Response,
  IncrementLoyaltyPointsRequestContent,
  InternalFailureErrorResponseContent,
  ListLoyaltyPromotions200Response,
  ListPlayerActivity200Response,
  ListPlayerActivitySummary200Response,
  Login200Response,
  LoginRefresh200Response,
  LoginRefreshRequestContent,
  LoginRequestContent,
  NotAuthorizedErrorResponseContent,
  SearchPlayer200Response,
  SearchPlayerEmail200Response,
  SearchPlayerEmailRequestContent,
  SearchPlayerMobile200Response,
  SearchPlayerMobileRequestContent,
  SearchPlayerRequestContent,
  SetLoyaltyPointsRequestContent,
  SetPasLastViewed200Response,
  SetPlayerDetailsRequestContent,
} from '../models';
// Import request parameter interfaces
import {
    AddNoteRequest,
    CreatePlayerRequest,
    CreatePlayerMobileRequest,
    DecrementLoyaltyPointsRequest,
    DeleteNoteRequest,
    IncrementLoyaltyPointsRequest,
    ListPlayerActivityRequest,
    LoginRequest,
    LoginRefreshRequest,
    SearchPlayerRequest,
    SearchPlayerEmailRequest,
    SearchPlayerMobileRequest,
    SetLoyaltyPointsRequest,
    SetPlayerDetailsRequest,
} from '..';

import { ResponseError } from '../runtime';
import { DefaultApi } from './DefaultApi';
import { DefaultApiDefaultContext } from "./DefaultApiClientProvider";

import {
    useQuery,
    UseQueryResult,
    UseQueryOptions,
    useInfiniteQuery,
    UseInfiniteQueryResult,
    UseInfiniteQueryOptions,
    useMutation,
    UseMutationOptions,
    UseMutationResult
} from "@tanstack/react-query";
import { createContext, useContext } from "react";

/**
 * Context for the API client used by the hooks.
 */
export const DefaultApiClientContext = createContext<DefaultApi | undefined>(undefined);

const NO_API_ERROR = new Error(`DefaultApi client missing. Please ensure you have instantiated the DefaultApiClientProvider with a client instance.`);

/**
 * useMutation hook for the AddNote operation
 */
export const useAddNote = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<AddNote200Response, TError, AddNoteRequest>, 'mutationFn'>
): UseMutationResult<AddNote200Response, TError, AddNoteRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: AddNoteRequest) => api.addNote(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the CreatePlayer operation
 */
export const useCreatePlayer = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<CreatePlayer200Response, TError, CreatePlayerRequest>, 'mutationFn'>
): UseMutationResult<CreatePlayer200Response, TError, CreatePlayerRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: CreatePlayerRequest) => api.createPlayer(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the CreatePlayerMobile operation
 */
export const useCreatePlayerMobile = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<CreatePlayerMobile200Response, TError, CreatePlayerMobileRequest>, 'mutationFn'>
): UseMutationResult<CreatePlayerMobile200Response, TError, CreatePlayerMobileRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: CreatePlayerMobileRequest) => api.createPlayerMobile(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the DecrementLoyaltyPoints operation
 */
export const useDecrementLoyaltyPoints = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<GenericSuccessResponseContent, TError, DecrementLoyaltyPointsRequest>, 'mutationFn'>
): UseMutationResult<GenericSuccessResponseContent, TError, DecrementLoyaltyPointsRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: DecrementLoyaltyPointsRequest) => api.decrementLoyaltyPoints(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the DeleteNote operation
 */
export const useDeleteNote = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<DeleteNote200Response, TError, DeleteNoteRequest>, 'mutationFn'>
): UseMutationResult<DeleteNote200Response, TError, DeleteNoteRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: DeleteNoteRequest) => api.deleteNote(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetBilling operation
 */
export const useGetBilling = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetBilling200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetBilling200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getBilling"], () => api.getBilling(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetLoyaltyPoints operation
 */
export const useGetLoyaltyPoints = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetLoyaltyPoints200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetLoyaltyPoints200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getLoyaltyPoints"], () => api.getLoyaltyPoints(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetNotes operation
 */
export const useGetNotes = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetNotes200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetNotes200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getNotes"], () => api.getNotes(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetPasLastViewed operation
 */
export const useGetPasLastViewed = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetPasLastViewed200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetPasLastViewed200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getPasLastViewed"], () => api.getPasLastViewed(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetPlayerActivityStatement operation
 */
export const useGetPlayerActivityStatement = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetPlayerActivityStatement200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetPlayerActivityStatement200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getPlayerActivityStatement"], () => api.getPlayerActivityStatement(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetPlayerActivityStatementFile operation
 */
export const useGetPlayerActivityStatementFile = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<Blob, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<Blob, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getPlayerActivityStatementFile"], () => api.getPlayerActivityStatementFile(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the GetPlayerDetails operation
 */
export const useGetPlayerDetails = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<GetPlayerDetails200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<GetPlayerDetails200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["getPlayerDetails"], () => api.getPlayerDetails(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the IncrementLoyaltyPoints operation
 */
export const useIncrementLoyaltyPoints = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<GenericSuccessResponseContent, TError, IncrementLoyaltyPointsRequest>, 'mutationFn'>
): UseMutationResult<GenericSuccessResponseContent, TError, IncrementLoyaltyPointsRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: IncrementLoyaltyPointsRequest) => api.incrementLoyaltyPoints(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the ListLoyaltyPromotions operation
 */
export const useListLoyaltyPromotions = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<ListLoyaltyPromotions200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ListLoyaltyPromotions200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["listLoyaltyPromotions"], () => api.listLoyaltyPromotions(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the ListPlayerActivity operation
 */
export const useListPlayerActivity = <TError = ResponseError>(
    params: ListPlayerActivityRequest,
    options?: Omit<UseQueryOptions<ListPlayerActivity200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ListPlayerActivity200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["listPlayerActivity", params], () => api.listPlayerActivity(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useQuery hook for the ListPlayerActivitySummary operation
 */
export const useListPlayerActivitySummary = <TError = ResponseError>(
    options?: Omit<UseQueryOptions<ListPlayerActivitySummary200Response, TError>, 'queryKey' | 'queryFn'>
): UseQueryResult<ListPlayerActivitySummary200Response, TError> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useQuery(["listPlayerActivitySummary"], () => api.listPlayerActivitySummary(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the Login operation
 */
export const useLogin = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<Login200Response, TError, LoginRequest>, 'mutationFn'>
): UseMutationResult<Login200Response, TError, LoginRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: LoginRequest) => api.login(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the LoginRefresh operation
 */
export const useLoginRefresh = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<LoginRefresh200Response, TError, LoginRefreshRequest>, 'mutationFn'>
): UseMutationResult<LoginRefresh200Response, TError, LoginRefreshRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: LoginRefreshRequest) => api.loginRefresh(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the SearchPlayer operation
 */
export const useSearchPlayer = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<SearchPlayer200Response, TError, SearchPlayerRequest>, 'mutationFn'>
): UseMutationResult<SearchPlayer200Response, TError, SearchPlayerRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: SearchPlayerRequest) => api.searchPlayer(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the SearchPlayerEmail operation
 */
export const useSearchPlayerEmail = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<SearchPlayerEmail200Response, TError, SearchPlayerEmailRequest>, 'mutationFn'>
): UseMutationResult<SearchPlayerEmail200Response, TError, SearchPlayerEmailRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: SearchPlayerEmailRequest) => api.searchPlayerEmail(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the SearchPlayerMobile operation
 */
export const useSearchPlayerMobile = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<SearchPlayerMobile200Response, TError, SearchPlayerMobileRequest>, 'mutationFn'>
): UseMutationResult<SearchPlayerMobile200Response, TError, SearchPlayerMobileRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: SearchPlayerMobileRequest) => api.searchPlayerMobile(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the SetLoyaltyPoints operation
 */
export const useSetLoyaltyPoints = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<GenericSuccessResponseContent, TError, SetLoyaltyPointsRequest>, 'mutationFn'>
): UseMutationResult<GenericSuccessResponseContent, TError, SetLoyaltyPointsRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: SetLoyaltyPointsRequest) => api.setLoyaltyPoints(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the SetPasLastViewed operation
 */
export const useSetPasLastViewed = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<SetPasLastViewed200Response, TError, void>, 'mutationFn'>
): UseMutationResult<SetPasLastViewed200Response, TError, void> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation(() => api.setPasLastViewed(), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

/**
 * useMutation hook for the SetPlayerDetails operation
 */
export const useSetPlayerDetails = <TError = ResponseError>(
    options?: Omit<UseMutationOptions<GenericSuccessResponseContent, TError, SetPlayerDetailsRequest>, 'mutationFn'>
): UseMutationResult<GenericSuccessResponseContent, TError, SetPlayerDetailsRequest> => {
    const api = useContext(DefaultApiClientContext);
    if (!api) {
        throw NO_API_ERROR;
    }
    return useMutation((params: SetPlayerDetailsRequest) => api.setPlayerDetails(params), {
        context: DefaultApiDefaultContext,
        ...options,
    });
};

